// ============================================
// ACHH WEBSITE CONTENT
// Edit all text content, images, and data here
// ============================================

const siteContent = {
  // ============================================
  // SITE CONFIGURATION
  // ============================================
  config: {
    siteName: "ACHH",
    siteFullName: "The African Cultural Heritage Hub Inc",
    logoUrl: "/images/logo.png",
    showFullName: true,
    paypalEmail: "websesame@gmail.com", // UPDATE THIS with your PayPal email
    contactEmail: "info@africanculturehub.org",
    contactPhone: "(470) 979-4694",
  },

  // ============================================
  // NAVIGATION MENU
  // ============================================
  navigation: {
    links: [
      { label: "Home", href: "#home" },
      { label: "About", href: "#about" },
      { label: "Projects", href: "#projects" },
      { label: "Get Involved", href: "#activities" },
      { label: "Publications", href: "#publications" },
      { label: "Volunteer", href: "#volunteer" },
      { label: "Contact", href: "#contact" },
      // This last link is rendered as a highlighted button, not a plain text link
      { label: "Donate", href: "#donate", isButton: true },
    ],
  },

  // ============================================
  // HERO SECTION
  // ============================================
  hero: {
    title: {
      line1: "African Cultural",
      line2: "Heritage Hub",
    },
    subtitle: "Preserving the Past • Celebrating the Present • Inspiring the Future",
    buttons: {
      primary: {
        text: "Explore Activities",
        link: "#activities",
      },
      secondary: {
        text: "Support Our Mission",
        link: "#donate",
      },
    },
  },

  // ============================================
  // ABOUT SECTION
  // ============================================
  about: {
    title: "Our Mission",
    paragraphs: [
      "Preserving African Cultural Heritage for a Global Generation",
      "The African Cultural Heritage Hub Inc. (ACHH) is a nonprofit organization dedicated to preserving and promoting African cultural heritage through storytelling, traditional arts, music, dance, documentaries, and youth programs. We provide cultural education experiences that connect African folklore, history, and artistic traditions with communities across the diaspora and beyond.",
      "Through workshops, heritage tours, publications, and community events, ACHH empowers young people and families to engage deeply with Africa's rich cultural legacy. Join us in sustaining African storytelling, safeguarding ancestral knowledge, and ensuring African traditions remain vibrant, accessible, and celebrated worldwide.",
    ],
  },

  // ============================================
  // FOUNDER'S STORY SECTION
  // ============================================
  founderStory: {
    title: "Our Founder's Story",
    eyebrow: "From the Director",
    name: "Rotimi Ogunjobi",
    role: "Founder & Executive Director",
    photoUrl: "/images/founder-rotimi.jpg",
    paragraphs: [
      "Rotimi Ogunjobi is a Nigerian-born author, folklorist, and media producer whose work spans African folktales, literary fiction, documentary film, and animated storytelling. His books include The Bata Dancer, The Dreamers — longlisted for the Commonwealth Book Prize — I Dead, and Somber City, alongside a growing catalog of more than 50 titles that carry African oral tradition to contemporary readers.",
      "He founded The African Cultural Heritage Hub out of a conviction that Africa's oral traditions deserve the same institutional permanence as its written history. What began as personal storytelling has grown into a nonprofit channeling decades of research and craft into documentary film, animated series, children's literature, and educational publishing — work that now reaches classrooms, public libraries, and streaming platforms around the world.",
    ],
    // ⭐ REPLACE this with a short, personal quote from Rotimi about why this work matters to him
    quote: "Every folktale carries a community's memory. My work is making sure that memory has somewhere permanent to live.",
  },

  // ============================================
  // IMPACT SECTION
  // ============================================
  impact: {
    title: "Our Impact",
    subtitle: "African storytelling, measured in reach",
    statement: "Every folktale we publish, every film we distribute, and every workshop we host carries African heritage a little further than it's traveled before.",
    stats: [
      {
        number: "35",
        label: "Episodes in our animated folktale anthology",
      },
      {
        number: "500,000+",
        label: "Institutions reached through our film & documentary distribution",
      },
      {
        number: "50+",
        label: "Published titles preserving African oral tradition",
      },
      {
        number: "5,000+",
        label: "People reached through workshops & community programs annually",
      },
      {
        number: "3+",
        label: "Years of active cultural production",
      },
      {
        number: "501(c)(3)",
        label: "Registered nonprofit in the State of Georgia, USA",
      },
    ],
  },

  // ============================================
  // CURRENT PROJECTS SECTION
  // ============================================
  projects: {
    title: "Current Projects",
    subtitle: "What we're producing, distributing, and building right now",
    items: [
      {
        icon: "🎨",
        accent: "ochre",
        category: "Traditional Arts & Crafts",
        status: "Ongoing Programming",
        description: "Hands-on workshops in traditional crafts, drumming, and dance led by master practitioners, keeping ancestral techniques alive for new generations of artists and audiences.",
      },
      {
        icon: "🎬",
        accent: "terracotta",
        category: "Documentary & Films",
        status: "Active Distribution",
        description: "Our documentary catalog — including the Legends & Legacy series and Voices from the Continent — explores African oral tradition on screen, distributed to schools and libraries nationwide. A short-film adaptation of the novella I Dead is currently in development.",
      },
      {
        icon: "📺",
        accent: "forest-green",
        category: "Children's TV & Book Series",
        status: "In Development",
        description: "KEMI & THE STORY ROAD, our animated series currently in development, brings African folktales to a new generation of viewers. It joins Magic Story Cloud, our 50-title illustrated children's book series.",
      },
      {
        icon: "🎶",
        accent: "clay-red",
        category: "Music & Dance",
        status: "Active Promotion",
        description: "We champion contemporary Afrobeats alongside traditional rhythm and dance, currently supporting the Cactus Town Crew single \"Nonstop Dancing\" and the album Songs of My Country, which sets Nigerian life and memory to music.",
      },
      {
        icon: "📚",
        accent: "deep-brown",
        category: "Publication & Literacy",
        status: "Active Catalog",
        description: "A growing library of folklore, fiction, and educational texts — from When the Sun Sleeps to our African Folktales and Storytelling course — designed to keep African storytelling traditions accessible in print, audio, and the classroom.",
      },
    ],
  },

  // ============================================
  // ACTIVITIES / GET INVOLVED SECTION
  // ============================================
  activities: {
    title: "Get Involved",
    subtitle: "Join us in celebrating and preserving African cultural heritage",
    items: [
      {
        icon: "🎨",
        title: "Cultural Workshops",
        description: "Join hands-on workshops in traditional crafts, drumming, dance, and storytelling led by master artisans and cultural practitioners.",
        imageUrl: "/images/activities/workshops.jpg",
      },
      {
        icon: "🗺️",
        title: "Heritage Tours",
        description: "Explore African cultural landmarks and historic sites through guided tours that bring history to life.",
        imageUrl: "/images/activities/tours.jpg",
      },
      {
        icon: "🌱",
        title: "Youth Programs",
        description: "Empowering the next generation through education, mentorship, and cultural immersion programs.",
        imageUrl: "/images/activities/youth.jpg",
      },
      {
        icon: "🎭",
        title: "Community Events",
        description: "Celebrate African culture through festivals, performances, exhibitions, and communal gatherings throughout the year.",
        imageUrl: "/images/activities/events.jpg",
      },
    ],
  },

  // ============================================
  // CURRENT PUBLICATIONS SECTION
  // ============================================
  publications: {
    title: "Current Publications",
    subtitle: "Explore our collection of films and folktale audio",
    // ========== DVDS (3) ==========
    dvds: [
      {
        title: "Legends & Legacy Part 1 : The Soul of African Storytelling",
        description: "A captivating exploration of Africa's rich folkloric heritage. The film delves into the cultural significance of oral traditions and tracks the evolution of African storytelling—from creation myths and animal fables to trickster tales and heroic legends.Running Time:60mins",
        price: "14.99",
        imageUrl: "/images/lal1.jpg",
        youtubeId: "S_olBF_uWog",
        stripeLink: "https://buy.stripe.com/7sY5kC4dI9Vn2t53kDeQM00",  // ⭐ REPLACE with your Stripe payment link
      },
      {
        title: "Legends and Legacy Part 2: The Magic of African Storytelling Traditions",
        description: "Explores the rich heritage of African folktales, revealing how timeless stories preserve history, teach values, and celebrate culture through powerful narration and vivid imagery. Running Time: 60mins",
        price: "14.99",
        imageUrl: "/images/lal2.jpg",
        youtubeId: "gORMb6wW3mU",
        stripeLink: "https://buy.stripe.com/6oU14mh0u1oRebN9J1eQM01",  // ⭐ REPLACE with your Stripe payment link
      },
      {
        title: "The Storyland Train",
        description: "Delivers captivating tales from many cultures. This enchanting eleven-story collection ignites imagination and wonder—perfect for curious minds and shared family moments! Running Time:60mins",
        price: "14.99",
        imageUrl: "/images/storytrain.jpg",
        youtubeId: "Z5JDV2hcuDs",
        stripeLink: "https://buy.stripe.com/fZu9ASaC63wZebNcVdeQM02",  // ⭐ REPLACE with your Stripe payment link
      },
    ],
    // ========== FOLKTALE CDS (3) ==========
    folktaleCDs: [
      {
        title: "Folktales of Africa 1",
        description: "Brings together fifteen treasured African folk stories filled with wisdom, humor, and life lessons. Rooted in oral tradition, these stories explore courage, kindness, perseverance, and community, preserving the spirit of African storytelling for readers of all ages.",
        price: "12.99",
        imageUrl: "/images/foa1.jpg",
        audioSample: "/samples/lazy-soule.mp3",
        stripeLink: "https://buy.stripe.com/aFacN4eSm9Vn2t53kDeQM03",  // ⭐ REPLACE with your Stripe payment link
      },
      {
        title: "Folktales of Africa 2",
        description: "Fifteen compelling folktales reveal the heart of African cultural heritage. Through memorable characters and meaningful journeys, this collection shares moral guidance, ancestral wisdom, and timeless truths that continue to shape values across generations.",
        price: "12.99",
        imageUrl: "/images/foa2.jpg",
        audioSample: "/samples/lazy-soule.mp3",
        stripeLink: "https://buy.stripe.com/aFa4gy6lQ3wZ1p1f3leQM04",  // ⭐ REPLACE with your Stripe payment link
      },
      {
        title: "Folktales of Africa 3",
        description: "Features fifteen vibrant folktales that spark imagination and celebrate the power of story. Filled with drama, insight, and unforgettable moments, this collection honors Africa's rich storytelling legacy while inspiring readers to reflect, dream, and grow",
        price: "12.99",
        imageUrl: "/images/foa3.jpg",
        audioSample: "/samples/lazy-soule.mp3",
        stripeLink: "https://buy.stripe.com/8x2eVc6lQgjL4BdbR9eQM05",  // ⭐ REPLACE with your Stripe payment link
      },
    ],
  },

  // ============================================
  // PARTNERSHIPS SECTION
  // ============================================
  partnerships: {
    title: "Our Partnerships",
    subtitle: "Organizations helping us bring African heritage further",
    items: [
      {
        name: "Films Media Group / Infobase Learning",
        role: "Educational Distribution Partner",
        description: "Brings our documentary and animated catalog to 500,000+ schools, colleges, and public libraries nationwide.",
      },
      {
        name: "OverDrive",
        role: "Library Distribution Partner",
        description: "Makes our book catalog available to public library patrons nationwide.",
      },
      {
        name: "Draft2Digital",
        role: "Publishing & Distribution Partner",
        description: "Powers ebook and print distribution of our catalog across major retailers.",
      },
    ],
  },

  // ============================================
  // VOLUNTEER SECTION
  // ============================================
  volunteer: {
    title: "Volunteer With Us",
    subtitle: "Share your time, skills, or expertise to help preserve and celebrate African cultural heritage",
    waysToHelp: [
      "Workshop & event support — help facilitate cultural workshops, heritage tours, and community events",
      "Content & storytelling — contribute writing, translation, narration, or research to our publications and films",
      "Technical & digital — support our website, digital archives, and social media presence",
      "Outreach & partnerships — help connect ACHH with schools, libraries, and community organizations",
    ],
    interestOptions: [
      "Workshop & Event Support",
      "Content & Storytelling",
      "Technical & Digital",
      "Outreach & Partnerships",
      "Other",
    ],
    // ⭐ REPLACE the form's submission handling — e.g. add a Formspree endpoint or a backend handler
    submitText: "Submit Volunteer Interest",
  },

  // ============================================
  // CONTACT SECTION
  // ============================================
  contact: {
    title: "Get in Touch",
    subtitle: "Have questions? Want to get involved? We'd love to hear from you!",
    email: "info@africanculturehub.org|tachhubb@gmail.com",
    phone: "(470) 979-4694",
    address: "Atlanta, GA 30324",
  },

  // ============================================
  // DONATE SECTION
  // ============================================
  donate: {
    title: "Support Our Mission",
    subtitle: "Your contribution helps preserve and celebrate African cultural heritage",
    infoTitle: "Why Your Support Matters",
    benefits: [
      "Fund educational programs and workshops for youth and communities",
      "Support artists, musicians, and cultural practitioners",
      "Preserve traditional knowledge and oral histories",
      "Create accessible cultural resources and publications",
    ],
    amountTitle: "Select an Amount",
    // Each amount now carries a short, illustrative description of its impact
    amounts: [
      { value: "25", label: "$25", impact: "Could help supply materials for one workshop participant" },
      { value: "50", label: "$50", impact: "Could help sponsor a child's access to our folktale series for a year" },
      { value: "100", label: "$100", impact: "Could help fund a community storytelling event" },
      { value: "250", label: "$250", impact: "Could help support production costs for a documentary segment" },
      { value: "500", label: "$500", impact: "Could help fund a full workshop series for a youth cohort" },
      { value: "", label: "Custom", impact: "Choose the amount that's right for you" },
    ],
    buttonText: "Donate Now via PayPal",
    taxNote: "The African Cultural Heritage Hub Inc. is a 501(c)(3) nonprofit organization. Your donation through this website is tax-deductible. ",
  },

  // ============================================
  // FOOTER
  // ============================================
  footer: {
    sections: [
      {
        title: "The African Cultural Heritage Hub Inc",
        content: "Preserving traditions, inspiring futures.",
      },
      {
        title: "Quick Links",
        links: [
          { text: "About Us", href: "#about" },
          { text: "Our Story", href: "#founder-story" },
          { text: "Impact", href: "#impact" },
          { text: "Projects", href: "#projects" },
          { text: "Get Involved", href: "#activities" },
          { text: "Publications", href: "#publications" },
          { text: "Partnerships", href: "#partnerships" },
          { text: "Volunteer", href: "#volunteer" },
          { text: "Donate", href: "#donate" },
        ],
      },
      {
        title: "Contact",
        lines: [
          "Email: info@africanculturehub.org | tachhubb@gmail.com",
          "Phone: (470) 979-4694",
          "Director: Rotimi Ogunjobi",
        ],
      },
    ],
    copyright: "© 2026 African Cultural Heritage Hub Inc. is a 501(c)(3) nonprofit organization incorporated as a charitable organization in the state of Georgia, USA. Control Number : 26115989 . All rights reserved.",
  },
};

export default siteContent;
